import { areaSection, imagerySection, mapsSection, transectSections } from '../utils/transectReportSections';

/**
 * Describes a Shore-R analysis as a document.
 * The measured part is the same as Shoreline's and comes from the shared sections; what differs
 * between the two modules is which imagery they draw on, not what a transect means.
 * The projected future coastlines are not in here. They are state of the analysis steps panel and
 * never reach the result page, which is where this export registers, so reporting them would mean
 * threading that state out of the component. Shore-R's own export dialog already writes them with
 * their horizons and labels - see {@link module:SHORER_GEOJSON}, which leaves them out for the
 * same reason.
 */

/**
 * @constant REPORT_MODULE
 * @description Module key, used to name the file and to register the export.
 * @memberof module:ShoreR
 */
export const REPORT_MODULE = 'shore_r';

/**
 * @function buildShoreRReport
 * @description The whole document, as data.
 * @param {Object} params
 * @param {Array<Array<Number>>} [params.coordinates] - Area of interest ring.
 * @param {Array<Array<Number>>} [params.baselineCoords] - Baseline vertices.
 * @param {Object} [params.shoreRData] - Holding `transectData` and `shorelinesGeoJSON`.
 * @param {Array<Object>} [params.chosenImages] - The imagery the analysis used.
 * @param {String} [params.satellite] - The satellite's name.
 * @param {String} [params.kmlFileName] - The uploaded area file.
 * @param {Array<Object>} [params.images] - Already loaded by {@link loadImageForReport}.
 * @param {Object} params.text - Every piece of wording, already translated.
 * @param {Function} [params.className] - Translates an Esteves class for the reader.
 * **Called by:
 * @see {ProcessingPage} - Shore-R's result page.
 * @memberof module:ShoreR
 * @returns {Object} A document description for {@link renderReport}.
 */
export const buildShoreRReport = ({
  coordinates,
  baselineCoords,
  shoreRData,
  chosenImages,
  satellite,
  kmlFileName,
  images,
  text,
  className,
}) => {
  const coastlines = shoreRData?.shorelinesGeoJSON?.features?.length ?? null;
  const imagery = imagerySection({ images: chosenImages, satellite, text });

  return {
    title: text.title,
    subtitle: text.subtitle,
    footer: text.footer,
    missingImageNote: text.missingImage,
    sections: [
      areaSection({ coordinates, baselineCoords, kmlFileName, text }),
      { ...imagery, rows: [...imagery.rows, [text.coastlines, coastlines]] },
      ...transectSections({ transects: shoreRData?.transectData, text, className }),
      mapsSection({ images, text }),
    ],
  };
};
